import api from "../../../shared/utils/api";

import { readToken } from '../../../shared/auth/session';

/** Same base URL pattern as AI generation service — handles local vs prod */
const AI_SERVICE_URL =
  import.meta.env.VITE_AI_SERVICE_URL || "http://localhost:9087";

const getToken = () => readToken();

// ---------------------------------------------------------------------------
// Lightweight tagged logging so failures are easy to trace in the console.
// Filter DevTools console by "[DocAPI]" to see the full documentation flow.
// ---------------------------------------------------------------------------
const TAG = "[DocAPI]";
const log = (...args) => console.log(TAG, ...args);
const logError = (label, url, err) =>
  console.error(TAG, label, url, {
    status: err?.status,
    message: err?.message,
    isNotFound: err?.isNotFound,
    isAccessDenied: err?.isAccessDenied,
    isServerError: err?.isServerError,
    data: err?.data,
  });

// NOTE: the shared `api` client (shared/utils/api.js) returns the parsed
// response body DIRECTLY (not an axios-style { data } wrapper), and
// DocumentationController returns the raw entity (no BaseAPIResponse envelope).
// So every handler below uses the response value as-is — never `res.data`.

const documentationApi = {
  async getDocumentation(entityType, entityId) {
    const url = `/api/documentation/${entityType}/${entityId}`;
    log("getDocumentation → GET", url);
    try {
      const res = await api.get(url);
      // 200 -> entity object; 204 No Content -> empty string / null.
      const doc = res && typeof res === "object" && res.id ? res : null;
      log("getDocumentation ✓", { hasDoc: !!doc, docId: doc?.id });
      return doc;
    } catch (err) {
      logError("getDocumentation ✗", url, err);
      throw err;
    }
  },

  async getByProject(projectId) {
    const url = `/api/documentation/project/${projectId}`;
    log("getByProject → GET", url);
    try {
      const res = await api.get(url);
      log("getByProject ✓", { count: Array.isArray(res) ? res.length : 0 });
      return res;
    } catch (err) {
      logError("getByProject ✗", url, err);
      throw err;
    }
  },

  async save(data) {
    const url = "/api/documentation";
    log("save → POST", url, {
      entityType: data.entityType,
      entityId: data.entityId,
      status: data.status || "DRAFT",
      changeSource: data.changeSource || "MANUAL",
      projectId: data.projectId,
    });
    try {
      const res = await api.post(url, {
        entityType: data.entityType,
        entityId: data.entityId,
        content: JSON.stringify(data.content),
        status: data.status || "DRAFT",
        projectId: data.projectId,
        changeSource: data.changeSource || "MANUAL",
      });
      log("save ✓", { docId: res?.id, version: res?.version });
      return res;
    } catch (err) {
      logError("save ✗", url, err);
      throw err;
    }
  },

  async updateStatus(docId, status) {
    const url = `/api/documentation/${docId}/status`;
    log("updateStatus → PATCH", url, { status });
    try {
      const res = await api.patch(url, { status });
      log("updateStatus ✓", { docId });
      return res;
    } catch (err) {
      logError("updateStatus ✗", url, err);
      throw err;
    }
  },

  async getHistory(docId) {
    const url = `/api/documentation/${docId}/history`;
    log("getHistory → GET", url);
    try {
      const res = await api.get(url);
      log("getHistory ✓", { count: Array.isArray(res) ? res.length : 0 });
      return res;
    } catch (err) {
      logError("getHistory ✗", url, err);
      throw err;
    }
  },

  async restoreVersion(docId, historyId) {
    const url = `/api/documentation/${docId}/restore/${historyId}`;
    log("restoreVersion → POST", url);
    try {
      const res = await api.post(url);
      log("restoreVersion ✓", { docId, historyId });
      return res;
    } catch (err) {
      logError("restoreVersion ✗", url, err);
      throw err;
    }
  },

  // Enable public sharing — mints/returns an opaque token (private-by-default).
  async enableShare(docId) {
    const url = `/api/documentation/${docId}/share`;
    log("enableShare → POST", url);
    try {
      const res = await api.post(url);
      log("enableShare ✓", { token: !!res?.token });
      return res; // { token, isPublished }
    } catch (err) {
      logError("enableShare ✗", url, err);
      throw err;
    }
  },

  async disableShare(docId) {
    const url = `/api/documentation/${docId}/share`;
    log("disableShare → DELETE", url);
    try {
      const res = await api.delete(url);
      log("disableShare ✓");
      return res; // { isPublished: false }
    } catch (err) {
      logError("disableShare ✗", url, err);
      throw err;
    }
  },

  async getCoverage(projectId) {
    const url = `/api/documentation/coverage/${projectId}`;
    log("getCoverage → GET", url);
    try {
      const res = await api.get(url);
      return res;
    } catch (err) {
      logError("getCoverage ✗", url, err);
      throw err;
    }
  },

  async getStatuses(projectId) {
    const url = `/api/documentation/statuses/${projectId}`;
    log("getStatuses → GET", url);
    try {
      const res = await api.get(url);
      return res;
    } catch (err) {
      logError("getStatuses ✗", url, err);
      throw err;
    }
  },

  async getLatestExecution(requestId) {
    const url = `/api/documentation/latest-execution/${requestId}`;
    log("getLatestExecution → GET", url);
    try {
      const res = await api.get(url);
      // 200 -> execution object; 204 -> null/empty
      const exec = res && typeof res === "object" && res.statusCode != null ? res : null;
      log("getLatestExecution ✓", { hasExecution: !!exec, status: exec?.statusCode });
      return exec;
    } catch (err) {
      logError("getLatestExecution ✗", url, err);
      return null; // non-fatal — docs still render without a real response example
    }
  },

  // Observed execution analytics (status-code distribution, success rate,
  // timing/size aggregates, categorized real examples). Returns null when the
  // request has never been executed (backend responds 204).
  async getAnalytics(requestId) {
    const url = `/api/documentation/analytics/${requestId}`;
    log("getAnalytics → GET", url);
    try {
      const res = await api.get(url);
      const analytics =
        res && typeof res === "object" && res.totalExecutions != null ? res : null;
      log("getAnalytics ✓", {
        hasAnalytics: !!analytics,
        total: analytics?.totalExecutions,
      });
      return analytics;
    } catch (err) {
      logError("getAnalytics ✗", url, err);
      return null; // non-fatal — Pre-AI page still renders without observed analytics
    }
  },

  async generate({ entityType, apiMetadata, context }) {
    const url = `${AI_SERVICE_URL}/api-docs/generate`;
    const token = getToken();
    log("generate → POST", url, { entityType, hasToken: !!token });
    let res;
    try {
      res = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({
          entity_type: entityType,
          api_metadata: apiMetadata || {},
          context: context || {},
        }),
      });
    } catch (networkErr) {
      console.error(TAG, "generate ✗ network", url, { message: networkErr?.message });
      throw networkErr;
    }

    if (!res.ok) {
      let body = null;
      try {
        body = await res.text();
      } catch {
        /* ignore */
      }
      console.error(TAG, "generate ✗", url, { status: res.status, body });
      throw new Error(`Generation failed: ${res.status}`);
    }

    // AI service responds with { content: {...} } (DocGenerateResponse).
    const data = await res.json();
    log("generate ✓", {
      contentKeys: data?.content ? Object.keys(data.content) : null,
    });
    return data.content;
  },
};

export default documentationApi;
